// One weather assessment: its points, the forecasts for them, the MWIS area
// forecast, the decision and its history, and how it's all judged. The
// assessment is fetched here and every child hands back the updated record the
// server returns, so there is one copy.
//
// The forecasts shown are the latest fetch's unless an earlier one is picked
// (`viewRunId`); that one is fetched on its own (GET .../runs/{id}) and drives
// the point forecasts, MWIS and the rules panel alike, so the page reads as it
// did then.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { notify } from "../notify";
import { useFullScreenDialog } from "../useFullScreenDialog";
import PointsSection from "./PointsSection";
import RoutesSection from "./RoutesSection";
import ForecastsSection, { SeverityChip } from "./ForecastsSection";
import MwisPanel from "./MwisPanel";
import DecisionPanel, { DecisionHistory } from "./DecisionPanel";
import RulesPanel from "./RulesPanel";
import { SAFETY_API, fmtWindow, fromLocalInput, toLocalInput } from "./format";

export default function AssessmentView({ id, config, onBack, onOpenEvent }) {
  const [a, setA] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [viewRunId, setViewRunId] = useState(null);   // null: the latest fetch
  const [viewed, setViewed] = useState(null);         // { run, forecasts } for viewRunId
  const [loadingRun, setLoadingRun] = useState(false);

  useEffect(() => {
    axios.get(`${SAFETY_API}/assessments/${id}`)
      .then(res => { setA(res.data); onOpenEvent?.(res.data.event_id); })
      .catch(() => onBack());
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewRunId === null) { setViewed(null); return; }
    let live = true;
    setLoadingRun(true);
    axios.get(`${SAFETY_API}/assessments/${id}/runs/${viewRunId}`)
      .then(res => { if (live) setViewed(res.data); })
      .catch(() => { if (live) setViewRunId(null); })
      .finally(() => { if (live) setLoadingRun(false); });
    return () => { live = false; };
  }, [id, viewRunId]);

  const fetchForecasts = useCallback(() => {
    setFetching(true);
    axios.post(`${SAFETY_API}/assessments/${id}/fetch`, {})
      .then(res => {
        setA(res.data);
        setViewRunId(null);
        const run = res.data.latest_run;
        const failed = run?.failed || 0;
        const said = run?.comparison?.headline || "Forecasts fetched.";
        notify(failed ? `${said} ${failed} provider call${failed === 1 ? "" : "s"} failed — see below.` : said,
               failed ? "warning" : "success");
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [id]);

  // From the decision history: show the fetch an entry was made on.
  const showRun = (runId) => {
    setViewRunId(runId);
    document.getElementById("safety-forecasts")?.scrollIntoView({ behavior: "smooth" });
  };

  const remove = () => {
    axios.delete(`${SAFETY_API}/assessments/${id}`).then(() => onBack()).catch(() => {});
  };

  if (!a || !config) return <CircularProgress />;
  const decided = a.status === "Decided";
  const shown = viewRunId !== null && viewed
    ? viewed
    : { run: a.latest_run, forecasts: a.forecasts };

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <IconButton onClick={onBack} aria-label="Back to assessments"><ArrowBackIcon /></IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="h5">{a.title}</Typography>
              <Chip size="small" label={a.status} color={decided ? "success" : "default"} />
              {a.latest_run && <SeverityChip severity={a.latest_run.summary?.severity} prefix="Latest forecast: " />}
              <Chip size="small" label="Beta" color="secondary" variant="outlined" />
            </Stack>
            <Typography color="text.secondary">{a.event_name} · {fmtWindow(a)}</Typography>
          </Box>
          <Tooltip title={decided ? "Reopen the assessment to edit it" : "Edit title and window"}>
            <span>
              <IconButton onClick={() => setEditing(true)} disabled={decided}><EditIcon /></IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete assessment">
            <IconButton onClick={() => setConfirmDelete(true)}><DeleteIcon /></IconButton>
          </Tooltip>
        </Stack>
        <Alert severity="info" sx={{ mt: 2 }}>
          Beta. The amber/red bands use default thresholds for evaluation, not Shelter Stone policy,
          and nothing here recommends a course of action — the decision is yours.
        </Alert>
      </Paper>

      <RoutesSection assessment={a} config={config} onChange={setA} />

      <PointsSection assessment={a} onChange={setA} />

      <ForecastsSection assessment={a} config={config} onChange={setA} onFetch={fetchForecasts}
                        fetching={fetching} shown={shown} viewRunId={viewRunId}
                        onViewRun={setViewRunId} loadingRun={loadingRun} />

      <MwisPanel assessment={a} forecasts={shown.forecasts} config={config} onChange={setA}
                 onFetch={fetchForecasts} fetching={fetching} />

      <DecisionPanel assessment={a} config={config} onChange={setA} />

      <DecisionHistory assessment={a} config={config} onViewRun={showRun} />

      <RulesPanel config={config} run={shown.run} />

      <EditDialog open={editing} assessment={a} onClose={() => setEditing(false)}
                  onSaved={(next) => { setA(next); setEditing(false); }} />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete this assessment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Its points, every forecast fetched for it (including stored MWIS PDFs) and the recorded
            decision will be deleted. The audit log keeps a record that it existed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function EditDialog({ open, assessment, onClose, onSaved }) {
  const fullScreen = useFullScreenDialog();
  const [form, setForm] = useState({ title: "", start: "", end: "" });

  useEffect(() => {
    if (open) setForm({ title: assessment.title, start: toLocalInput(assessment.window_start),
                        end: toLocalInput(assessment.window_end) });
  }, [open, assessment]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const valid = form.title.trim() && form.start && form.end && form.end > form.start;
  const save = () => {
    axios.put(`${SAFETY_API}/assessments/${assessment.id}`, {
      title: form.title.trim(), window_start: fromLocalInput(form.start), window_end: fromLocalInput(form.end),
    }).then(res => onSaved(res.data)).catch(() => {});
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Edit assessment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" value={form.title} onChange={set("title")} />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField label="From" type="datetime-local" value={form.start} onChange={set("start")}
                       slotProps={{ inputLabel: { shrink: true } }} />
            <TextField label="To" type="datetime-local" value={form.end} onChange={set("end")}
                       slotProps={{ inputLabel: { shrink: true } }}
                       error={!!form.start && !!form.end && form.end <= form.start} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
